import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth, API } from "@/App";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users,
  TrendingUp,
  Clock,
  AlertCircle,
  CheckCircle2,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  Calendar,
  ExternalLink,
  X,
  Loader2,
  Copy,
  ChevronDown,
  Search,
  Filter,
  Tag,
  MessageSquare,
  Bell,
  MoreHorizontal,
  CheckSquare,
  ArrowRight,
  Trash2,
  Plus,
  Building2,
  Briefcase,
  User,
  CircleDot,
  Trophy,
  XCircle,
  Send,
  ClipboardCopy,
  Download,
  FileDown
} from "lucide-react";
import AppSidebar from "@/components/AppSidebar";

// Status configuration
const statusConfig = {
  new: { color: "bg-blue-100 text-blue-700 border-blue-200", label: "New", icon: CircleDot },
  contacted: { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Contacted", icon: Mail },
  qualified: { color: "bg-purple-100 text-purple-700 border-purple-200", label: "Qualified", icon: CheckCircle2 },
  won: { color: "bg-green-100 text-green-700 border-green-200", label: "Won", icon: Trophy },
  lost: { color: "bg-gray-100 text-gray-500 border-gray-200", label: "Lost", icon: XCircle }
};

const urgencyConfig = {
  "Priority": { color: "bg-red-100 text-red-700 border-red-200", label: "Priority", icon: AlertCircle },
  "High": { color: "bg-orange-100 text-orange-700 border-orange-200", label: "High", icon: Clock },
  "Moderate": { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Moderate", icon: Clock },
  "Low": { color: "bg-green-100 text-green-700 border-green-200", label: "Low", icon: CheckCircle2 },
  "Immediate": { color: "bg-red-100 text-red-700 border-red-200", label: "Priority", icon: AlertCircle },
  "Medium": { color: "bg-yellow-100 text-yellow-700 border-yellow-200", label: "Moderate", icon: Clock }
};

const getScoreColor = (score) => {
  if (score >= 90) return "score-high text-white";
  if (score >= 70) return "score-high text-white";
  if (score >= 50) return "score-medium text-white";
  return "score-low text-white";
};

const TAG_COLORS = [
  "bg-blue-100 text-blue-700",
  "bg-green-100 text-green-700",
  "bg-purple-100 text-purple-700",
  "bg-pink-100 text-pink-700",
  "bg-orange-100 text-orange-700",
  "bg-cyan-100 text-cyan-700"
];

export default function PipelinePage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // CRM State
  const [viewMode, setViewMode] = useState("list");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scoreFilter, setScoreFilter] = useState("all");
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [allTags, setAllTags] = useState([]);
  
  // Modal State
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [tagModalOpen, setTagModalOpen] = useState(false);
  const [bulkActionModalOpen, setBulkActionModalOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [newReminder, setNewReminder] = useState({ date: "", note: "" });
  const [newTag, setNewTag] = useState("");
  const [bulkStatus, setBulkStatus] = useState("");
  
  // Email Templates State
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [schedulingLinks, setSchedulingLinks] = useState({});
  
  // CSV Export State
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    startDate: "",
    endDate: "",
    status: ""
  });
  const [exporting, setExporting] = useState(false);
  
  // Use subscription context instead of local state
  const { subscriptionStatus } = useSubscription();

  useEffect(() => {
    fetchData();
    fetchTags();
    fetchEmailTemplates();
    fetchSchedulingLinks();
    // No need to fetch subscription status - comes from context
  }, []);

  const fetchSchedulingLinks = async () => {
    try {
      const response = await axios.get(`${API}/business/scheduling-links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchedulingLinks(response.data || {});
    } catch (error) {
      console.error("Failed to fetch scheduling links");
    }
  };

  const getFirstSchedulingLink = () => {
    if (schedulingLinks.google_calendar) return schedulingLinks.google_calendar;
    if (schedulingLinks.calendly) return schedulingLinks.calendly;
    if (schedulingLinks.square_appointments) return schedulingLinks.square_appointments;
    return null;
  };

  const fetchData = async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/leads/stats/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await axios.get(`${API}/leads/tags`, { headers: { Authorization: `Bearer ${token}` } });
      setAllTags(response.data.tags || []);
    } catch (error) {
      console.error("Failed to fetch tags");
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await axios.get(`${API}/email/templates`, { headers: { Authorization: `Bearer ${token}` } });
      setEmailTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Failed to fetch email templates");
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (exportFilters.startDate) params.append("start_date", new Date(exportFilters.startDate).toISOString());
      if (exportFilters.endDate) params.append("end_date", new Date(exportFilters.endDate).toISOString());
      if (exportFilters.status && exportFilters.status !== "all") params.append("status", exportFilters.status);
      
      const response = await axios.get(`${API}/leads/export/csv?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Leads exported successfully");
      setExportModalOpen(false);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error("No leads found matching your filters");
      } else {
        toast.error("Failed to export leads");
      }
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.put(`${API}/leads/${leadId}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(leads.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
      if (selectedLead?.id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
      fetchData();
      toast.success(`Lead marked as ${statusConfig[newStatus].label}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleEmailLead = async (lead, templateId = null) => {
    const leadEmail = getLeadEmail(lead);
    if (!leadEmail) {
      toast.error("No email address found for this lead");
      return;
    }
    
    setEmailLoading(true);
    try {
      let subject = "";
      let body = "";
      let templateName = "custom";
      
      if (templateId) {
        const response = await axios.get(`${API}/leads/${lead.id}/email-template/${templateId}`, { headers: { Authorization: `Bearer ${token}` } });
        subject = response.data.subject;
        body = response.data.body;
        templateName = response.data.template_name;
      }
      
      // Build mailto URL - only include subject/body if they have values
      let mailtoUrl = `mailto:${encodeURIComponent(leadEmail)}`;
      const params = [];
      if (subject) params.push(`subject=${encodeURIComponent(subject)}`);
      if (body) params.push(`body=${encodeURIComponent(body)}`);
      if (params.length > 0) {
        mailtoUrl += `?${params.join('&')}`;
      }
      
      await axios.post(`${API}/leads/${lead.id}/email-activity`, { action_type: "email_draft_opened", template_name: templateName }, { headers: { Authorization: `Bearer ${token}` } });
      
      window.location.href = mailtoUrl;
      toast.success("Opening email client...");
    } catch (error) {
      toast.error("Failed to prepare email");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleCopyTemplate = async (lead, templateId) => {
    setEmailLoading(true);
    try {
      const response = await axios.get(`${API}/leads/${lead.id}/email-template/${templateId}`, { headers: { Authorization: `Bearer ${token}` } });
      const templateText = `Subject: ${response.data.subject}\n\n${response.data.body}`;
      await navigator.clipboard.writeText(templateText);
      await axios.post(`${API}/leads/${lead.id}/email-activity`, { action_type: "template_copied", template_name: response.data.template_name }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Template copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy template");
    } finally {
      setEmailLoading(false);
    }
  };

  const openEmailModal = (lead) => {
    setSelectedLead(lead);
    setSelectedTemplate(null);
    setEmailModalOpen(true);
  };

  const addNote = async () => {
    if (!selectedLead || !newNote.trim()) return;
    try {
      await axios.post(`${API}/leads/${selectedLead.id}/notes`, { content: newNote }, { headers: { Authorization: `Bearer ${token}` } });
      const updatedLead = { ...selectedLead, notes: [...(selectedLead.notes || []), { content: newNote, created_at: new Date().toISOString() }] };
      setSelectedLead(updatedLead);
      setLeads(leads.map(l => l.id === selectedLead.id ? updatedLead : l));
      setNewNote("");
      setNoteModalOpen(false);
      toast.success("Note added");
    } catch (error) {
      toast.error("Failed to add note");
    }
  };

  const setReminder = async () => {
    if (!selectedLead || !newReminder.date) return;
    try {
      await axios.post(`${API}/leads/${selectedLead.id}/reminders`, { due_date: newReminder.date, note: newReminder.note || "Follow up" }, { headers: { Authorization: `Bearer ${token}` } });
      setNewReminder({ date: "", note: "" });
      setReminderModalOpen(false);
      toast.success("Reminder set");
    } catch (error) {
      toast.error("Failed to set reminder");
    }
  };

  const addTag = async () => {
    if (!selectedLead || !newTag.trim()) return;
    const currentTags = selectedLead.tags || [];
    if (currentTags.includes(newTag.trim())) {
      toast.error("Tag already exists");
      return;
    }
    const updatedTags = [...currentTags, newTag.trim()];
    try {
      await axios.put(`${API}/leads/${selectedLead.id}/tags`, { tags: updatedTags }, { headers: { Authorization: `Bearer ${token}` } });
      const updatedLead = { ...selectedLead, tags: updatedTags };
      setSelectedLead(updatedLead);
      setLeads(leads.map(l => l.id === selectedLead.id ? updatedLead : l));
      setNewTag("");
      setTagModalOpen(false);
      if (!allTags.includes(newTag.trim())) {
        setAllTags([...allTags, newTag.trim()].sort());
      }
      toast.success("Tag added");
    } catch (error) {
      toast.error("Failed to add tag");
    }
  };

  const removeTag = async (tagToRemove) => {
    if (!selectedLead) return;
    const updatedTags = (selectedLead.tags || []).filter(t => t !== tagToRemove);
    try {
      await axios.put(`${API}/leads/${selectedLead.id}/tags`, { tags: updatedTags }, { headers: { Authorization: `Bearer ${token}` } });
      const updatedLead = { ...selectedLead, tags: updatedTags };
      setSelectedLead(updatedLead);
      setLeads(leads.map(l => l.id === selectedLead.id ? updatedLead : l));
      toast.success("Tag removed");
    } catch (error) {
      toast.error("Failed to remove tag");
    }
  };

  const toggleLeadSelection = (leadId) => {
    setSelectedLeads(prev => prev.includes(leadId) ? prev.filter(id => id !== leadId) : [...prev, leadId]);
  };

  const selectAllLeads = () => {
    if (selectedLeads.length === filteredLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(filteredLeads.map(l => l.id));
    }
  };

  const bulkUpdateStatus = async () => {
    if (!bulkStatus || selectedLeads.length === 0) return;
    try {
      await axios.post(`${API}/leads/bulk-update`, { lead_ids: selectedLeads, status: bulkStatus }, { headers: { Authorization: `Bearer ${token}` } });
      setLeads(leads.map(l => selectedLeads.includes(l.id) ? { ...l, status: bulkStatus } : l));
      setSelectedLeads([]);
      setBulkActionModalOpen(false);
      setBulkStatus("");
      fetchData();
      toast.success(`Updated ${selectedLeads.length} leads`);
    } catch (error) {
      toast.error("Failed to update leads");
    }
  };

  // Filtered leads
  const filteredLeads = useMemo(() => {
    return leads.filter(lead => {
      const matchesSearch = searchQuery === "" || 
        (lead.form_data["Full Name"] || lead.form_data["Name"] || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (lead.form_data["Email"] || lead.form_data["email"] || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (lead.form_data["Company Name"] || lead.form_data["company"] || "").toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === "all" || (lead.status || "new") === statusFilter;
      
      const matchesScore = scoreFilter === "all" ||
        (scoreFilter === "priority" && lead.ai_score >= 85) ||
        (scoreFilter === "high" && lead.ai_score >= 70 && lead.ai_score < 85) ||
        (scoreFilter === "moderate" && lead.ai_score >= 50 && lead.ai_score < 70) ||
        (scoreFilter === "low" && lead.ai_score < 50);
      
      return matchesSearch && matchesStatus && matchesScore;
    });
  }, [leads, searchQuery, statusFilter, scoreFilter]);

  const openLeadDetail = (lead) => {
    setSelectedLead(lead);
  };

  const getLeadName = (lead) => {
    return lead.form_data["Full Name"] || lead.form_data["Name"] || lead.form_data["name"] || "Unknown";
  };

  const getLeadEmail = (lead) => {
    return lead.form_data["Email"] || lead.form_data["email"] || "";
  };

  const getLeadCompany = (lead) => {
    return lead.form_data["Company Name"] || lead.form_data["company"] || lead.form_data["Company"] || "";
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex" >
      <AppSidebar user={user} onLogout={handleLogout} />

      <main className="flex-1 ml-64 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-foreground" >
              Pipeline
            </h1>
            <p className="text-muted-foreground">
              Manage your leads and track conversions.
            </p>
          </div>
          
          {/* Submission Limit Warning Banner */}
          {subscriptionStatus && (() => {
            const leadsUsed = subscriptionStatus.is_trialing 
              ? subscriptionStatus.trial_leads_used 
              : subscriptionStatus.leads_used;
            const leadsLimit = subscriptionStatus.is_trialing 
              ? subscriptionStatus.trial_leads_limit 
              : subscriptionStatus.leads_limit;
            const percentage = leadsLimit > 0 ? Math.round((leadsUsed / leadsLimit) * 100) : 0;
            const remaining = Math.max(0, leadsLimit - leadsUsed);
            
            // Show warning at 80%, 90%, or 100%
            if (percentage >= 80 && leadsLimit > 0) {
              const isAtLimit = percentage >= 100;
              const isAlmostFull = percentage >= 90 && !isAtLimit;
              
              return (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`mb-6 p-4 rounded-xl border ${
                    isAtLimit 
                      ? 'bg-red-50 border-red-200' 
                      : isAlmostFull 
                        ? 'bg-orange-50 border-orange-200' 
                        : 'bg-yellow-50 border-yellow-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isAtLimit ? 'bg-red-100' : isAlmostFull ? 'bg-orange-100' : 'bg-yellow-100'
                      }`}>
                        <AlertCircle className={`w-5 h-5 ${
                          isAtLimit ? 'text-red-600' : isAlmostFull ? 'text-orange-600' : 'text-yellow-600'
                        }`} />
                      </div>
                      <div>
                        <p className={`font-semibold ${
                          isAtLimit ? 'text-red-800' : isAlmostFull ? 'text-orange-800' : 'text-yellow-800'
                        }`}>
                          {isAtLimit 
                            ? 'Submission limit reached!' 
                            : isAlmostFull 
                              ? `Only ${remaining} submission${remaining !== 1 ? 's' : ''} remaining` 
                              : `${percentage}% of your ${subscriptionStatus.is_trialing ? 'trial' : 'monthly'} limit used`
                          }
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {isAtLimit
                            ? 'New form submissions will be blocked until you upgrade.'
                            : `You've used ${leadsUsed} of ${leadsLimit} submissions this ${subscriptionStatus.is_trialing ? 'trial period' : 'month'}.`
                          }
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate('/settings?tab=billing')} 
                      size="sm"
                      className={`rounded-full ${
                        isAtLimit 
                          ? 'bg-red-600 hover:bg-red-700' 
                          : isAlmostFull 
                            ? 'bg-orange-600 hover:bg-orange-700' 
                            : 'bg-yellow-600 hover:bg-yellow-700'
                      }`}
                    >
                      {isAtLimit ? 'Upgrade Now' : 'View Plans'}
                    </Button>
                  </div>
                  {/* Progress bar */}
                  <div className="mt-3 h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all ${
                        isAtLimit ? 'bg-red-500' : isAlmostFull ? 'bg-orange-500' : 'bg-yellow-500'
                      }`}
                      style={{ width: `${Math.min(percentage, 100)}%` }}
                    />
                  </div>
                </motion.div>
              );
            }
            return null;
          })()}

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                {[
                  { label: "Total Leads", value: stats?.total_leads || 0, icon: Users, color: "bg-primary/10 text-primary" },
                  { label: "New", value: stats?.status_breakdown?.new || 0, icon: CircleDot, color: "bg-blue-100 text-blue-600" },
                  { label: "Contacted", value: stats?.status_breakdown?.contacted || 0, icon: Mail, color: "bg-yellow-100 text-yellow-600" },
                  { label: "Qualified", value: stats?.status_breakdown?.qualified || 0, icon: CheckCircle2, color: "bg-purple-100 text-purple-600" },
                  { label: "Won", value: stats?.status_breakdown?.won || 0, icon: Trophy, color: "bg-green-100 text-green-600" }
                ].map((stat, index) => (
                  <motion.div
                    key={stat.label}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="rounded-2xl border-none shadow-sm">
                      <CardContent className="p-4 flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.color}`}>
                          <stat.icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold">{stat.value}</p>
                          <p className="text-xs text-muted-foreground">{stat.label}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>

              {/* Filters */}
              <Card className="rounded-2xl border-none shadow-sm mb-6">
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        placeholder="Search leads..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 rounded-xl"
                        data-testid="search-leads-input"
                      />
                    </div>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-40 rounded-xl" data-testid="status-filter">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        {Object.entries(statusConfig).map(([key, config]) => (
                          <SelectItem key={key} value={key}>{config.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={scoreFilter} onValueChange={setScoreFilter}>
                      <SelectTrigger className="w-40 rounded-xl" data-testid="score-filter">
                        <SelectValue placeholder="All Priorities" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Priorities</SelectItem>
                        <SelectItem value="priority">Priority (85+)</SelectItem>
                        <SelectItem value="high">High (70-84)</SelectItem>
                        <SelectItem value="moderate">Moderate (50-69)</SelectItem>
                        <SelectItem value="low">Low (&lt;50)</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex items-center gap-2 bg-muted rounded-xl p-1">
                      <Button
                        variant={viewMode === "list" ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setViewMode("list")}
                        className="rounded-lg"
                      >
                        List
                      </Button>
                      <Button
                        variant={viewMode === "pipeline" ? "default" : "ghost"}
                        size="sm"
                        onClick={() => setViewMode("pipeline")}
                        className="rounded-lg"
                      >
                        <ArrowRight className="w-4 h-4 mr-1" />
                        Pipeline
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Leads List */}
              <Card className="rounded-2xl border-none shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-lg">Leads ({filteredLeads.length})</h3>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setExportModalOpen(true)}
                        className="rounded-full"
                        data-testid="export-csv-btn"
                      >
                        <FileDown className="w-4 h-4 mr-2" />
                        Export CSV
                      </Button>
                      {filteredLeads.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={selectAllLeads} data-testid="select-all-btn">
                          {selectedLeads.length === filteredLeads.length ? "Deselect All" : "Select All"}
                        </Button>
                      )}
                    </div>
                  </div>

                  {filteredLeads.length === 0 ? (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                      <h3 className="font-semibold text-lg mb-2">No leads yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Share your intake forms to start receiving leads.
                      </p>
                      <Button onClick={() => navigate("/form-builder")} className="rounded-full">
                        Manage Your Forms
                      </Button>
                    </div>
                  ) : viewMode === "list" ? (
                    <div className="space-y-3">
                      {filteredLeads.map((lead) => (
                        <motion.div
                          key={lead.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className={`p-4 rounded-xl border ${selectedLeads.includes(lead.id) ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'} transition-all cursor-pointer`}
                          data-testid={`lead-item-${lead.id}`}
                        >
                          <div className="flex items-start gap-4">
                            <Checkbox
                              checked={selectedLeads.includes(lead.id)}
                              onCheckedChange={() => toggleLeadSelection(lead.id)}
                              className="mt-1"
                            />
                            <div className={`w-12 h-12 rounded-xl ${getScoreColor(lead.ai_score)} flex items-center justify-center font-bold text-lg flex-shrink-0`}>
                              {lead.ai_score != null ? Math.ceil(lead.ai_score) : "—"}
                            </div>
                            <div className="flex-1 min-w-0" onClick={() => openLeadDetail(lead)}>
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-foreground">{getLeadName(lead)}</h4>
                                <Badge className={`${statusConfig[lead.status || "new"].color} rounded-full text-xs`}>
                                  {statusConfig[lead.status || "new"].label}
                                </Badge>
                                {lead.priority_label && urgencyConfig[lead.priority_label] && (
                                  <Badge className={`${urgencyConfig[lead.priority_label].color} rounded-full text-xs`}>
                                    {urgencyConfig[lead.priority_label].label}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                {lead.summary || lead.ai_summary || "No summary available"}
                              </p>
                              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <CircleDot className="w-3 h-3" />
                                  {statusConfig[lead.status || "new"].label}
                                </span>
                                <span>{new Date(lead.created_at).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openLeadDetail(lead)}>
                                  <User className="w-4 h-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                {getLeadEmail(lead) && (
                                  <DropdownMenuItem onClick={() => openEmailModal(lead)} data-testid={`email-lead-btn-${lead.id}`}>
                                    <Send className="w-4 h-4 mr-2" />
                                    Email Lead
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {Object.entries(statusConfig).map(([key, config]) => (
                                  <DropdownMenuItem 
                                    key={key}
                                    onClick={() => updateLeadStatus(lead.id, key)}
                                    disabled={lead.status === key || (!lead.status && key === "new")}
                                  >
                                    <config.icon className="w-4 h-4 mr-2" />
                                    Mark as {config.label}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    // Pipeline View
                    <div className="grid grid-cols-5 gap-4">
                      {Object.entries(statusConfig).map(([status, config]) => (
                        <div key={status} className="space-y-3">
                          <div className={`p-3 rounded-xl ${config.color} text-center`}>
                            <config.icon className="w-4 h-4 mx-auto mb-1" />
                            <p className="font-medium text-sm">{config.label}</p>
                            <p className="text-xs opacity-75">
                              {filteredLeads.filter(l => (l.status || "new") === status).length} leads
                            </p>
                          </div>
                          <ScrollArea className="h-[400px]">
                            <div className="space-y-2 pr-2">
                              {filteredLeads
                                .filter(l => (l.status || "new") === status)
                                .map(lead => (
                                  <div
                                    key={lead.id}
                                    onClick={() => openLeadDetail(lead)}
                                    className="p-3 bg-white rounded-xl border border-border hover:border-primary/30 cursor-pointer transition-all"
                                  >
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className={`w-8 h-8 rounded-lg ${getScoreColor(lead.ai_score)} flex items-center justify-center font-bold text-sm`}>
                                        {lead.ai_score != null ? Math.ceil(lead.ai_score) : "—"}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-sm truncate">{getLeadName(lead)}</p>
                                      </div>
                                    </div>
                                    <p className="text-xs text-muted-foreground line-clamp-2">
                                      {lead.summary || lead.ai_summary || ""}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          </ScrollArea>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>

      {/* Bulk Actions Bar */}
      <AnimatePresence>
        {selectedLeads.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-foreground text-background px-6 py-4 rounded-2xl shadow-xl flex items-center gap-4"
            data-testid="bulk-actions-bar"
          >
            <span className="font-medium">{selectedLeads.length} selected</span>
            <Separator orientation="vertical" className="h-6 bg-background/20" />
            <Button variant="secondary" size="sm" onClick={() => setBulkActionModalOpen(true)} className="rounded-full">
              Change Status
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedLeads([])} className="text-background/70 hover:text-background rounded-full">
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Lead Detail Modal */}
      <Dialog open={!!selectedLead && !emailModalOpen} onOpenChange={() => setSelectedLead(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedLead && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 rounded-2xl ${getScoreColor(selectedLead.ai_score)} flex items-center justify-center font-bold text-2xl`}>
                    {selectedLead.ai_score != null ? Math.ceil(selectedLead.ai_score) : "—"}
                  </div>
                  <div>
                    <DialogTitle className="text-xl">{getLeadName(selectedLead)}</DialogTitle>
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                      {getLeadEmail(selectedLead) && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{getLeadEmail(selectedLead)}</span>}
                      {getLeadCompany(selectedLead) && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{getLeadCompany(selectedLead)}</span>}
                    </div>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Status */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-muted-foreground">Status:</span>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <Button
                      key={key}
                      variant={selectedLead.status === key || (!selectedLead.status && key === "new") ? "default" : "outline"}
                      size="sm"
                      className={`rounded-full ${selectedLead.status === key || (!selectedLead.status && key === "new") ? config.color : ''}`}
                      onClick={() => updateLeadStatus(selectedLead.id, key)}
                    >
                      <config.icon className="w-3 h-3 mr-1" />
                      {config.label}
                    </Button>
                  ))}
                </div>

                {/* Email Actions */}
                {getLeadEmail(selectedLead) && (
                  <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-blue-900">Email Lead</span>
                      </div>
                      <span className="text-sm text-blue-600">{getLeadEmail(selectedLead)}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        className="rounded-full bg-blue-600 hover:bg-blue-700"
                        onClick={() => handleEmailLead(selectedLead)}
                        disabled={emailLoading}
                      >
                        <Send className="w-3 h-3 mr-1" />
                        Compose Email
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="rounded-full">
                            <ClipboardCopy className="w-3 h-3 mr-1" />
                            Use Template
                            <ChevronDown className="w-3 h-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-56">
                          {emailTemplates.map((template) => (
                            <DropdownMenuItem 
                              key={template.id}
                              onClick={() => handleEmailLead(selectedLead, template.id)}
                            >
                              <span className="font-medium">{template.name}</span>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm text-muted-foreground">Tags</span>
                    <Button variant="ghost" size="sm" onClick={() => setTagModalOpen(true)} className="h-7 text-xs">
                      <Plus className="w-3 h-3 mr-1" />
                      Add Tag
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {(selectedLead.tags || []).map((tag, index) => (
                      <Badge key={tag} className={`${TAG_COLORS[index % TAG_COLORS.length]} rounded-full flex items-center gap-1`}>
                        {tag}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => removeTag(tag)} />
                      </Badge>
                    ))}
                    {(!selectedLead.tags || selectedLead.tags.length === 0) && (
                      <span className="text-sm text-muted-foreground">No tags</span>
                    )}
                  </div>
                </div>

                {/* AI Summary */}
                <div className="bg-muted/50 rounded-xl p-4">
                  <h4 className="font-medium mb-2">AI Summary</h4>
                  <p className="text-sm text-muted-foreground">{selectedLead.summary || selectedLead.ai_summary || "No AI summary available."}</p>
                  {selectedLead.recommended_action && (
                    <p className="text-sm text-primary mt-2 flex items-center gap-1">
                      <ArrowRight className="w-4 h-4" />
                      {selectedLead.recommended_action}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setNoteModalOpen(true)} className="rounded-full">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Add Note
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setReminderModalOpen(true)} className="rounded-full">
                    <Bell className="w-3 h-3 mr-1" />
                    Set Reminder
                  </Button>
                </div>

                {/* Notes */}
                {(selectedLead.notes || []).length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Notes</h4>
                    <div className="space-y-2">
                      {(selectedLead.notes || []).map((note, index) => (
                        <div key={index} className="bg-muted/30 rounded-lg p-3">
                          <p className="text-sm">{note.content}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(note.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Form Data */}
                <div>
                  <h4 className="font-medium mb-2">Submission Details</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(selectedLead.form_data || {}).map(([key, value]) => (
                      <div key={key} className="bg-muted/30 rounded-lg p-3">
                        <p className="text-xs text-muted-foreground uppercase">{key}</p>
                        <p className="text-sm font-medium">{String(value)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Note Modal */}
      <Dialog open={noteModalOpen} onOpenChange={setNoteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Enter your note..."
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            rows={4}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteModalOpen(false)}>Cancel</Button>
            <Button onClick={addNote}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder Modal */}
      <Dialog open={reminderModalOpen} onOpenChange={setReminderModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Due Date</Label>
              <Input
                type="datetime-local"
                value={newReminder.date}
                onChange={(e) => setNewReminder({ ...newReminder, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input
                placeholder="Reminder note..."
                value={newReminder.note}
                onChange={(e) => setNewReminder({ ...newReminder, note: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminderModalOpen(false)}>Cancel</Button>
            <Button onClick={setReminder}>Set Reminder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag Modal */}
      <Dialog open={tagModalOpen} onOpenChange={setTagModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tag</DialogTitle>
          </DialogHeader>
          <Input
            placeholder="Enter tag name..."
            value={newTag}
            onChange={(e) => setNewTag(e.target.value)}
            list="existing-tags"
          />
          <datalist id="existing-tags">
            {allTags.filter(t => !selectedLead?.tags?.includes(t)).map(tag => (
              <option key={tag} value={tag} />
            ))}
          </datalist>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTagModalOpen(false)}>Cancel</Button>
            <Button onClick={addTag}>Add Tag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Action Modal */}
      <Dialog open={bulkActionModalOpen} onOpenChange={setBulkActionModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Status for {selectedLeads.length} Leads</DialogTitle>
          </DialogHeader>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger>
              <SelectValue placeholder="Select new status" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>
                  <div className="flex items-center gap-2">
                    <config.icon className="w-4 h-4" />
                    {config.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkActionModalOpen(false)}>Cancel</Button>
            <Button onClick={bulkUpdateStatus} disabled={!bulkStatus}>
              Apply to {selectedLeads.length} Leads
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={emailModalOpen} onOpenChange={setEmailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Email {selectedLead ? getLeadName(selectedLead) : "Lead"}
            </DialogTitle>
            <DialogDescription>
              Choose a template to email this lead. Opens your default email client.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {selectedLead && getLeadEmail(selectedLead) && (
              <div className="bg-muted/50 rounded-lg p-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium">{getLeadEmail(selectedLead)}</span>
              </div>
            )}
            <div className="space-y-2">
              <Label>Select a template</Label>
              <div className="space-y-2">
                {emailTemplates.map((template) => (
                  <div
                    key={template.id}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedTemplate === template.id 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-border hover:border-blue-200'
                    }`}
                    onClick={() => setSelectedTemplate(template.id)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium">{template.name}</span>
                      {selectedTemplate === template.id && (
                        <CheckCircle2 className="w-4 h-4 text-blue-600" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      Subject: {template.subject}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setEmailModalOpen(false)}>Cancel</Button>
            <Button 
              variant="outline"
              onClick={() => {
                if (selectedLead && selectedTemplate) {
                  handleCopyTemplate(selectedLead, selectedTemplate);
                  setEmailModalOpen(false);
                }
              }}
              disabled={!selectedTemplate || emailLoading}
            >
              <ClipboardCopy className="w-4 h-4 mr-1" />
              Copy Template
            </Button>
            <Button 
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => {
                if (selectedLead) {
                  handleEmailLead(selectedLead, selectedTemplate);
                  setEmailModalOpen(false);
                }
              }}
              disabled={emailLoading}
            >
              <Send className="w-4 h-4 mr-1" />
              {selectedTemplate ? 'Open Email Client' : 'Compose Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Export Modal */}
      <Dialog open={exportModalOpen} onOpenChange={setExportModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="w-5 h-5 text-[#1a4d2e]" />
              Export Leads to CSV
            </DialogTitle>
            <DialogDescription>
              Download your leads as a CSV file. Use filters to export a specific date range or status.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={exportFilters.startDate}
                  onChange={(e) => setExportFilters({ ...exportFilters, startDate: e.target.value })}
                  className="mt-1"
                  data-testid="export-start-date"
                />
              </div>
              <div>
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={exportFilters.endDate}
                  onChange={(e) => setExportFilters({ ...exportFilters, endDate: e.target.value })}
                  className="mt-1"
                  data-testid="export-end-date"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="export-status">Status Filter</Label>
              <Select 
                value={exportFilters.status || "all"} 
                onValueChange={(value) => setExportFilters({ ...exportFilters, status: value === "all" ? "" : value })}
              >
                <SelectTrigger className="mt-1" data-testid="export-status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  {Object.entries(statusConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-lg">
              <p className="flex items-center gap-2">
                <Download className="w-4 h-4" />
                Export includes all custom form fields and lead details.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleExportCSV}
              disabled={exporting}
              className="bg-[#1a4d2e] hover:bg-[#2d6a4f]"
              data-testid="export-download-btn"
            >
              {exporting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
